import UploadCaseDef from "../../types/UploadCaseDef";
import ky from "ky";
import getRemoteURL from "../../get-remote-url";
import streamCompressInstance from "../stream-compress-instance";
import * as p10Staging from "../../opfs/p10-staging";
import { UploadCredentials } from "./get-upload-creds";
import getInstanceFileKey from "./get-instance-file-key";
// import getS3InstanceURL from "./get-s3-instance-url";
import { S3Client } from "@aws-sdk/client-s3";
import uploadInstance from "./upload-instance";
import { progressBar, updateStatus, addLog } from "../../logger";
import pool from "@ricokahler/pool";
import {
  collapseSeriesParts,
  displaySetsForUpload,
} from "../../display-sets";
// import {
//   CaseDefinitionDocument,
//   NewCaseDefinitionDocument,
//   SeriesDocument,
//   StudyDocument,
// } from "pacsbin-core/src/db/models/CaseDefinition";

export default async function createCaseDef(
  caseDef: UploadCaseDef,
  uploadCredentials: UploadCredentials,
  s3Client?: S3Client
  //   userId: string
) {
  const totalInstances = caseDef.studies.reduce((numInstances, study) => {
    const studyInstanceCount = study.series.reduce((numInstances, series) => {
      return numInstances + series.instances.length;
    }, 0);
    return numInstances + studyInstanceCount;
  }, 0);

  updateStatus("Uploading studies...");
  progressBar.value.start(totalInstances);

  // Progress accounting across the whole upload. Each fully-uploaded instance is
  // worth 1 unit; in-flight instances contribute their current fraction so a
  // single large (e.g. multiframe) instance moves the bar instead of looking
  // hung. Up to maxConcurrency instances are in flight at once, so we sum their
  // partial fractions on top of the completed count.
  let completedInstances = 0;
  const inFlightFractions = new Map<string, number>();
  const refreshProgress = () => {
    let sum = completedInstances;
    for (const fraction of inFlightFractions.values()) sum += fraction;
    progressBar.value.numDone = sum;
  };

  // Create new case definition with series instances empty
  const remoteCaseDef = (await ky
    .post(getRemoteURL("api/casedefinition/new"), {
      json: {
        description: caseDef.metadata?.description,
        caseData: [
          {
            fieldType: 5,
            content: caseDef.metadata?.notes,
          },
        ],
        diagnosis: caseDef.metadata?.diagnosis,
        tags: caseDef.metadata?.tags,
      },
      credentials: "include",
    })
    .json()) as { _id: string; shortid: string };

  if (!remoteCaseDef) {
    addLog("error", "Failed to create remote case");
    throw new Error("Failed to create remote case");
  }

  caseDef._id = remoteCaseDef._id;
  caseDef.shortid = remoteCaseDef.shortid;

  // Create remote study for each study in case definition
  for (let study of caseDef.studies) {
    // Create remote study for each study in case definition
    const remoteStudy = (await ky
      .post(getRemoteURL(`api/casedefinition/${remoteCaseDef._id}/study/new`), {
        json: {
          caseDefinitionId: remoteCaseDef._id,
          description: study.metadata?.description || study.studyDescription,
          uploadStatus: "processing",
          storageProvider: uploadCredentials.storageProvider,
          bucket: uploadCredentials.bucket,
          meta: {
            phiAttestation: caseDef.phiAttestation,
            source: caseDef.orgId || "web",
            uploaderVersion: __APP_VERSION__,
          },
          studyDate: study.studyDate,
          // series: study.series.map((series) => {
          // 	return {
          // 		seriesUID: series.seriesInstanceUID,
          // 		label: series.seriesDescription,
          // 		modality: series.modality || "UK",
          // 		dicomTags: [
          // 			{
          // 				name: "SeriesInstanceUID",
          // 				value: series.seriesInstanceUID,
          // 			},
          // 		],
          // 		instances: [],
          // 	};
          // }),
        },
        credentials: "include",
      })
      .json()) as { _id: string; shortid: string };
    study.shortid = remoteStudy.shortid;
    study._id = remoteStudy._id;
  }

  for (let study of caseDef.studies) {
    // Process each series of the study, compressing and uploading to S3.
    // Parts of a split series are collapsed back into one series here: they share
    // a SeriesInstanceUID, which keys both the remote series record and the
    // storage prefix that instance filenames are numbered under. Uploading them
    // separately would duplicate the record and have each part's `1.dcm.gz`
    // overwrite the last. See display-sets.ts.
    //
    // The split itself isn't thrown away: it's sent with the series so pacsbin
    // opens it already split. Computed before collapsing, while the parts are
    // still separate.
    const uploadDisplaySets = displaySetsForUpload(study.series);
    for (let series of collapseSeriesParts(study.series)) {
      const displaySets = uploadDisplaySets.get(
        series.seriesInstanceUID || series.uid
      );
      const instances = await pool({
        collection: series.instances,
        maxConcurrency: 3,
        task: async (instance, index) => {
          if (!study.shortid)
            throw new Error("No remote study shortid defined for upload task.");
          // Hydrate compressed bytes: either from OPFS (spooled during the write
          // step) or already in memory (the fallback when spooling was
          // unavailable).
          if (!instance.instanceData.dicomP10Compressed) {
            if (instance.instanceData.p10Staged) {
              instance.instanceData.dicomP10Compressed =
                (await p10Staging.loadCompressed(instance.uid)) ?? undefined;
            }
            if (!instance.instanceData.dicomP10Compressed) {
              // Spooled bytes are gone (storage error since the write step). Rebuild
              // them: frames are decoded from their source files on demand, so this
              // reproduces the exact same output rather than failing the instance.
              addLog(
                "warn",
                `${instance.imageId}: staged file unavailable, rebuilding before upload`
              );
              instance.instanceData.p10Staged = false;
              await streamCompressInstance(instance);
            }
          }
          let fileKey: string;
          // If the storage provider is litevna, we don't calculate the file key
          // ahead of time, but get the instance uid from the upload response
          if (uploadCredentials.storageProvider === "litevna") {
            const uploadResponse = await uploadInstance({
              instance,
              uploadCredentials,
              // fileKey: instance.uid,
              // s3Client,
              studyShortid: study.shortid,
              onUploadProgress: (fraction) => {
                inFlightFractions.set(instance.uid, fraction);
                refreshProgress();
              },
            });
            instance.instanceData.dicomP10Compressed = undefined; // free Blob after upload
            if (instance.instanceData.p10Staged) {
              await p10Staging.evict(instance.uid);
              instance.instanceData.p10Staged = false;
            }
            const {
              studyInstanceUID,
              seriesInstanceUID,
              sopInstanceUID,
              // instancePath,
            } = uploadResponse;

            // Generate file key with the generated study uid from litevna
            fileKey = getInstanceFileKey({
              storageProvider: uploadCredentials.storageProvider,
              prefix: uploadCredentials.prefix,
              shortid: study.shortid,
              studyUID: studyInstanceUID,
              seriesUID: seriesInstanceUID,
              instanceUID: sopInstanceUID,
              // filename,
            });
          } else {
            // For s3 or r2, calculate the file key before uploading
            const filename = `${index + 1}.dcm.gz`;
            fileKey = getInstanceFileKey({
              storageProvider: uploadCredentials.storageProvider,
              prefix: uploadCredentials.prefix,
              shortid: study.shortid,
              studyUID: study.studyInstanceUID,
              seriesUID: series.seriesInstanceUID,
              instanceUID: instance.sopInstanceUID,
              filename,
            });
            await uploadInstance({
              instance,
              uploadCredentials,
              fileKey,
              s3Client,
              studyShortid: study.shortid,
            });
            instance.instanceData.dicomP10Compressed = undefined; // free Blob after upload
            if (instance.instanceData.p10Staged) {
              await p10Staging.evict(instance.uid);
              instance.instanceData.p10Staged = false;
            }
          }

          inFlightFractions.delete(instance.uid);
          completedInstances += 1;
          refreshProgress();

          return {
            fileKey,
            originalSize: instance.instanceData.p10Size!.decompressed!,
            compressedSize: instance.instanceData.p10Size!.compressed!,
            // The uploaded file's frame count: prepareAnonymizedDataset writes
            // NumberOfFrames from the same list, after any trim.
            numberOfFrames: instance.frames.length,
            displaySetPart: displaySets?.partOfInstance.get(instance.uid),
          };
        },
      });

      // Create new remote series
      await ky.post(
        getRemoteURL(
          `api/casedefinition/${remoteCaseDef._id}/study/${study._id}/series/new`
        ),
        {
          json: {
            studyId: study._id,
            seriesUID: series.seriesInstanceUID,
            label: series.seriesDescription,
            modality: series.modality || "UK",
            dicomTags: [
              {
                name: "SeriesInstanceUID",
                value: series.seriesInstanceUID,
              },
            ],
            instances,
            // Each part's base label; older pacsbin versions ignore this. See
            // displaySetsForUpload.
            displaySets: displaySets?.parts,
          },
          credentials: "include",
        }
      );
    }

    // Update remote study with completed status
    await ky.post(
      getRemoteURL(
        `api/casedefinition/${remoteCaseDef._id}/study/${study._id}`
      ),
      {
        json: {
          uploadStatus: "complete",
        },
        credentials: "include",
      }
    );
  }

  // Update playlists with added case
  if (caseDef.metadata?.playlists) {
    for (let playlist of caseDef.metadata.playlists) {
      try {
        await ky.post(getRemoteURL("api/playlist/add-cases"), {
          json: {
            playlistId: playlist,
            caseIds: [remoteCaseDef._id],
          },
          credentials: "include",
        });
        addLog("info", `Added case to playlist with id ${playlist}`);
      } catch (err) {
        addLog("error", `Error adding case to playlist: ${err}`);
        console.error("Error adding case to playlist");
        console.error(err);
      }
    }
  }

  addLog("info", "Study upload complete.");

  return remoteCaseDef;
}
